import React from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Autocomplete,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search, FilterListOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const PhoneCatalogFilters = ({
  countries,
  cities,
  providers,
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  // Parse comma-separated filter values into arrays
  const selectedCountries = filters.country
    ? filters.country.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const selectedCities = filters.city
    ? filters.city.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const selectedProviders = filters.provider
    ? filters.provider.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  const hasActiveFilters =
    filters.country || filters.city || filters.provider || filters.search || filters.top_only || filters.free_for_trial_only;

  const autocompleteInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853' },
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: 'none',
        p: 2,
        mb: 2.5,
        bgcolor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Country — multi-select Autocomplete */}
        <Autocomplete
          multiple
          size="small"
          options={countries.map((c) => c.name)}
          value={selectedCountries}
          onChange={(_, vals) => onFilterChange('country', vals.join(','))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  size="small"
                  {...rest}
                  sx={{ height: 22, fontSize: 12 }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedCountries.length === 0 ? t('phoneCatalog.country', 'Country') : ''}
              sx={autocompleteInputSx}
            />
          )}
          sx={{ minWidth: 220, maxWidth: 400 }}
        />

        {/* City — multi-select Autocomplete */}
        <Autocomplete
          multiple
          size="small"
          options={cities || []}
          value={selectedCities}
          onChange={(_, vals) => onFilterChange('city', vals.join(','))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  size="small"
                  {...rest}
                  sx={{ height: 22, fontSize: 12 }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedCities.length === 0 ? t('phoneCatalog.city', 'City') : ''}
              sx={autocompleteInputSx}
            />
          )}
          sx={{ minWidth: 180, maxWidth: 350 }}
        />

        {/* Provider — multi-select Autocomplete */}
        <Autocomplete
          multiple
          size="small"
          options={providers}
          value={selectedProviders}
          onChange={(_, vals) => onFilterChange('provider', vals.join(','))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  size="small"
                  {...rest}
                  sx={{ height: 22, fontSize: 12 }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedProviders.length === 0 ? t('phoneCatalog.allProviders', 'Provider') : ''}
              sx={autocompleteInputSx}
            />
          )}
          sx={{ minWidth: 180, maxWidth: 350 }}
        />

        {/* Search */}
        <TextField
          size="small"
          placeholder={t('phoneCatalog.searchCityCode', 'Search city / code...')}
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            ...autocompleteInputSx,
          }}
        />

        {/* Top Countries Only */}
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.top_only}
              onChange={(e) => onFilterChange('top_only', e.target.checked)}
              size="small"
              sx={{ '&.Mui-checked': { color: '#00C853' } }}
            />
          }
          label={
            <Box component="span" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {t('phoneCatalog.topCountriesOnly', 'Top Countries Only')}
            </Box>
          }
        />

        {/* Free Trial Only */}
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.free_for_trial_only}
              onChange={(e) => onFilterChange('free_for_trial_only', e.target.checked)}
              size="small"
              sx={{ '&.Mui-checked': { color: '#00C853' } }}
            />
          }
          label={
            <Box component="span" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              {t('phoneCatalog.freeForTrialOnly', 'Free Trial Only')}
            </Box>
          }
        />

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<FilterListOff />}
            onClick={onClearFilters}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': { color: '#00C853' },
            }}
          >
            {t('phoneCatalog.clearFilters', 'Clear Filters')}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default PhoneCatalogFilters;
